import { useState, useEffect, useRef } from "react";
import "./NightmarkLanding.css";

const CheckIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const faqData = [
  { q: "When does Martyn's Law come into force?", a: "Use the landing page line from the document: Spring 2027 is the earliest expected date, giving organizations time to move from awareness into evidence backed readiness." },
  { q: "Who enforces it?", a: "The source copy positions the Security Industry Authority as regulator, with enforcement powers and a guidance role that operators should track closely." },
  { q: "Does it apply to student accommodation?", a: "Yes - the outreach draft explicitly states that higher education settings and relevant off site student accommodation can fall within scope." },
  { q: "Is having SIA-licensed door staff enough?", a: "No - the campaign makes a clear distinction between staffing choices and the documented responsibilities that sit with the responsible person under the Act." },
];

function FaqItem({ item, isOpen, onToggle }) {
  const bodyRef = useRef(null);
  return (
    <div className={`nm-faq-item${isOpen ? " open" : ""}`}>
      <div className="nm-faq-summary" onClick={onToggle} role="button" tabIndex={0} onKeyDown={e => e.key === "Enter" && onToggle()}>
        <span className="nm-faq-q">{item.q}</span>
        <span className="nm-faq-icon">{isOpen ? "−" : "+"}</span>
      </div>
      <div className="nm-faq-body" ref={bodyRef}>{item.a}</div>
    </div>
  );
}

export default function NightmarkLanding() {
  const [navOpen, setNavOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  // Close nav on outside click
  useEffect(() => {
    if (!navOpen) return;
    const handler = (e) => {
      if (!e.target.closest(".nm-container.nm-nav")) {
        setNavOpen(false);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [navOpen]);

  const handleNavLink = () => setNavOpen(false);

  return (
    <>
      {/* HEADER */}
      <header className="nm-header">
        <div className="nm-container nm-nav">
          <a href="#top" className="nm-brand" aria-label="Nightmark Group home">
            <img src="/brand_logo.png" alt="Nightmark Group logo" style={{ height: "5rem", width: "auto", display: "block" }} />
          </a>
          <button
            className="nm-nav-toggle"
            aria-label="Open navigation"
            aria-expanded={navOpen}
            onClick={() => setNavOpen(v => !v)}
          >
            <span /><span /><span />
          </button>
          <nav className={`nm-nav-links${navOpen ? " open" : ""}`} aria-label="Primary">
            {[["#why-now", "WHY NOW"], ["#scope", "WHO IS IN SCOPE"], ["#delivery", "WHAT WE DELIVER"], ["#faq", "FAQ"]].map(([href, label]) => (
              <a key={href} href={href} onClick={handleNavLink}>{label}</a>
            ))}
            <a className="nm-btn nm-btn-primary nm-nav-cta nm-mobile-only nm-contact-btn" href="#contact" onClick={handleNavLink}>Book a readiness call</a>
          </nav>
          <div className="nm-nav-actions">
            <a className="nm-btn nm-btn-primary nm-nav-cta nm-desktop-only" href="#contact">Book a readiness call</a>
          </div>
        </div>
      </header>

      <main id="main">
        {/* HERO */}
        <section className="nm-hero" id="top">
          <div className="nm-container nm-hero-grid">
            <div>
              <span className="nm-eyebrow">Martyn's Law readiness</span>
              <h1>Ready for Martyn's Law. Proven by Nightmark.</h1>
              <p>The Terrorism (Protection of Premises) Act 2025 is expected to come into force in Spring 2027. This page turns your outreach copy into a clear landing experience built around audit, training, procedures and monitored response.</p>
              <div className="nm-hero-actions">
                <a className="nm-btn nm-btn-secondary" href="#contact">Book a 20-minute readiness call</a>
                <a className="nm-btn nm-btn-secondary" href="#guide">Download the free Readiness Guide</a>
              </div>
              <div className="nm-trust-strip" aria-label="Trust indicators">
                {["SIA-approved contractor", "ISO 9001", "BS 7858", "BS 10800", "Trio ARC 24/7 monitored"].map(pill => (
                  <span key={pill} className="nm-trust-pill">{pill}</span>
                ))}
              </div>
            </div>
            <aside className="nm-hero-panel" aria-label="Campaign highlights">
              <div className="nm-stat-card">
                <div className="nm-stat-top">
                  <div>
                    <div className="nm-eyebrow">Window to prepare</div>
                    <div className="nm-stat-number">11</div>
                  </div>
                  <p className="nm-stat-label">Roughly eleven months to move from awareness to evidence if teams act before Spring 2027.</p>
                </div>
                <div className="nm-mini-grid">
                  {[
                    { title: "Standard tier", desc: "200–799 capacity, documented public protection procedures." },
                    { title: "Enhanced tier", desc: "800+ capacity, formal plan, measures, risk assessment and named senior lead." },
                    { title: "30 days", desc: "Readiness Audit timeline with board ready readout." },
                    { title: "One contract", desc: "Audit, procedures, training and monitored response under one group." },
                  ].map(c => (
                    <div key={c.title} className="nm-mini-card">
                      <strong>{c.title}</strong>
                      <span>{c.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </section>

        {/* WHY NOW */}
        <section className="nm-section" id="why-now">
          <div className="nm-container nm-two-col">
            <div>
              <span className="nm-eyebrow">Why this matters now</span>
              <h2 className="nm-section-title">Start before urgency becomes panic.</h2>
            </div>
            <div className="nm-cards">
              <article className="nm-card">
                <h3>From awareness to evidence</h3>
                <p>The strongest hook in the source document is timing: Royal Assent is done, the guidance cycle is active, and operators have a narrowing runway to turn broad awareness into documented procedures, training records and evidence packs.</p>
              </article>
              <article className="nm-card">
                <h3>Designed for board level conversations</h3>
                <p>This layout gives the campaign a more executive tone by making the time pressure, tiering logic and delivery model easy to scan in a few seconds.</p>
              </article>
            </div>
          </div>
        </section>

        {/* SCOPE */}
        <section className="nm-section" id="scope">
          <div className="nm-container">
            <span className="nm-eyebrow">Who is in scope</span>
            <h2 className="nm-section-title">Simple tiering for venues, estates and multi site portfolios.</h2>
            <p className="nm-section-copy">Use this structure to help venue operators, PBSA groups and education estates self identify quickly before booking a call.</p>
            <div className="nm-scope-table">
              <table>
                <thead>
                  <tr>
                    <th>If your premises reasonably hosts...</th>
                    <th>You are...</th>
                    <th>You must...</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Fewer than 200</td>
                    <td><span className="nm-tier nm-tier-out">Out of scope</span></td>
                    <td>Still advisable to follow standard tier procedures, especially in higher risk locations.</td>
                  </tr>
                  <tr>
                    <td>200–799</td>
                    <td><span className="nm-tier nm-tier-standard">Standard tier</span></td>
                    <td>Notify the SIA and put public protection procedures in place for evacuation, invacuation, lockdown and communication.</td>
                  </tr>
                  <tr>
                    <td>800+</td>
                    <td><span className="nm-tier nm-tier-enhanced">Enhanced tier</span></td>
                    <td>All of the above, plus a documented security plan, formal risk assessment, proportionate measures and a named senior responsible person.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* DELIVERY */}
        <section className="nm-section" id="delivery">
          <div className="nm-container nm-two-col">
            <div>
              <span className="nm-eyebrow">What Nightmark delivers</span>
              <h2 className="nm-section-title">A practical path from first audit to ongoing compliance.</h2>
              <div className="nm-checklist">
                {[
                  "Audit, procedures, training and monitored response are grouped into one coherent commercial offer.",
                  "The copy is especially strong for operators who want evidence packs and board ready outputs rather than generic advisory language.",
                  "Trio ARC and standards language help position Nightmark as an operator led partner, not just a policy writer.",
                ].map((text, i) => (
                  <div key={i} className="nm-check">
                    <CheckIcon />
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="nm-steps">
              {[
                { idx: "01 · Fixed-fee audit", title: "Readiness Audit", desc: "Tier sites, map every gap and produce a prioritised 90 day plan in around 30 days end to end." },
                { idx: "02 · Full programme", title: "Compliance Programme", desc: "Move from audit to procedures, plans, training, tabletop exercise and a clause by clause evidence binder." },
                { idx: "03 · Retained support", title: "Managed Compliance Retainer", desc: "Quarterly reviews, guidance updates, refresher training and monitored response through and beyond enforcement. " },
                { idx: "04 · Modular training", title: "Tabletop Exercises", desc: "Standalone or embedded training modules aligned to the SIA facing evidence standard outlined in the campaign copy." },
              ].map(s => (
                <article key={s.idx} className="nm-step">
                  <div className="nm-step-index">{s.idx}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* WHY NIGHTMARK */}
        <section className="nm-section">
          <div className="nm-container nm-two-col">
            <div>
              <span className="nm-eyebrow">Why Nightmark</span>
              <h2 className="nm-section-title">Built around Operation, Evidence and Accountability.</h2>
            </div>
            <div className="nm-cards">
              {[
                { title: "Operated, not just written", desc: "The source material emphasises that Nightmark runs its own Alarm Receiving Centre through Trio Solutions, which gives the offer more credibility than a policy only advisory proposition." },
                { title: "Standards-led delivery", desc: "ISO 9001, BS 7858 and BS 10800 are framed as evidence standards that support how the SIA is likely to inspect readiness." },
                { title: "Sector relevance", desc: "Night time economy venues, student accommodation and education estates are presented as the three most exposed sectors in the campaign narrative, making them natural audience blocks for targeted versions of this page." },
              ].map(c => (
                <article key={c.title} className="nm-card">
                  <h3>{c.title}</h3>
                  <p>{c.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* GUIDE */}
        <section className="nm-section" id="guide">
          <div className="nm-container nm-guide">
            <div>
              <span className="nm-eyebrow" style={{ background: "rgba(255,255,255,0.1)", color: "var(--color-text-inverse)" }}>Lead magnet</span>
              <h3>The Martyn's Law Readiness Guide - Free, 14 pages.</h3>
              <p>Tier by tier requirements, the five questions a board should be asking today and a 90 day readiness checklist. Replace the CTA target with your live guide URL, PDF link  or embedded form.</p>
            </div>
            <a className="nm-btn nm-btn-primary" href="#contact">Download the Guide</a>
          </div>
        </section>

        {/* FAQ */}
        <section className="nm-section" id="faq">
          <div className="nm-container nm-two-col">
            <div>
              <span className="nm-eyebrow">FAQ</span>
              <h2 className="nm-section-title">Answer the objections before they slow down the sale.</h2>
            </div>
            <div className="nm-faq-list">
              {faqData.map((item, i) => (
                <FaqItem
                  key={i}
                  item={item}
                  isOpen={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="nm-section" id="contact">
          <div className="nm-container nm-final-cta">
            <div>
              <span className="nm-eyebrow" style={{ background: "rgba(255,255,255,0.1)", color: "var(--color-text-inverse)" }}>Final CTA</span>
              <h2 className="nm-section-title" style={{ maxWidth: "14ch", marginTop: "var(--space-4)", color: "var(--color-text-inverse)" }}>Start with a 20 Minute call.</h2>
              <p>We'll tier the portfolio, flag the highest risk gaps and help you decide whether a Readiness Audit is worth commissioning. Replace the button below with your live booking link or embed form.</p>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-3)" }}>
              <a className="nm-btn nm-btn-primary" href="mailto:hello@nightmarkgroup.co.uk">Email Nightmark</a>
              <a className="nm-btn nm-btn-secondary" href="#top">Back to top</a>
            </div>
          </div>
        </section>
      </main>

      <footer className="nm-footer">
        <div className="nm-container nm-footer-grid">
          <div>Nightmark Group</div>
         
        </div>
      </footer>
    </>
  );
}